/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {PDFElementPersist, PDFElementSubType, PDFElementType} from '@dvrd/dvr-controls';

import PDFTemplate from './pdfTemplateModel';

interface Props extends BaseProps {
    element_type?: PDFElementType;
    width?: number;
    height?: number;
    left?: number;
    top?: number;
    options?: Record<string, any>;
    template_id?: string;
    template?: PDFTemplate | any;
    persistent?: PDFElementPersist;
    linked_id?: string;
    sub_type?: PDFElementSubType | null;
}

interface Fields extends BaseFields {
    element_type: PDFElementType;
    width: number;
    height: number;
    left: number;
    top: number;
    options: Record<string, any>;
    template_id: string;
    template: PDFTemplate | null;
    persistent: PDFElementPersist;
    linked_id: string;
    sub_type: PDFElementSubType | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    element_type: PDFElementType.TEXT,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    options: {},
    template_id: '',
    template: null,
    persistent: PDFElementPersist.NOT_PERSISTENT,
    linked_id: '',
    sub_type: null
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'template'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    width: createFieldDef(ModelFieldType.NUMBER),
    height: createFieldDef(ModelFieldType.NUMBER),
    left: createFieldDef(ModelFieldType.NUMBER),
    top: createFieldDef(ModelFieldType.NUMBER),
    template_id: createFieldDef(ModelFieldType.STRING),
    options: createFieldDef(ModelFieldType.OBJECT),
    element_type: createFieldDef(ModelFieldType.STRING),
    persistent: createFieldDef(ModelFieldType.STRING, PDFElementPersist.NOT_PERSISTENT),
    linked_id: createFieldDef(ModelFieldType.STRING),
    sub_type: createFieldDef(ModelFieldType.STRING, null, true)
};

const ignoreProps: Array<keyof Props> = [];

export default class PDFElement extends BaseModel<Props, Fields> {
    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get elementType(): PDFElementType {
        return this.fields.element_type;
    }

    set elementType(element_type: PDFElementType) {
        this.fields.element_type = element_type;
    }

    get width(): number {
        return this.fields.width;
    }

    set width(width: number) {
        this.fields.width = width;
    }

    get height(): number {
        return this.fields.height;
    }

    set height(height: number) {
        this.fields.height = height;
    }

    get left(): number {
        return this.fields.left;
    }

    set left(left: number) {
        this.fields.left = left;
    }

    get top(): number {
        return this.fields.top;
    }

    set top(top: number) {
        this.fields.top = top;
    }

    get options(): Record<string, any> {
        return this.fields.options;
    }

    set options(options: Record<string, any>) {
        this.fields.options = options;
    }

    get templateId(): string {
        return this.fields.template_id;
    }

    set templateId(template_id: string) {
        this.fields.template_id = template_id;
    }

    get template(): PDFTemplate | null {
        return this.fields.template;
    }

    set template(template: PDFTemplate | null) {
        this.fields.template = template;
        if (template) this.templateId = template.id;
    }

    get persistent(): PDFElementPersist {
        return this.fields.persistent;
    }

    set persistent(persist: PDFElementPersist) {
        this.fields.persistent = persist;
    }

    get linkedId() {
        return this.fields.linked_id;
    }

    set linkedId(id: string) {
        this.fields.linked_id = id;
    }

    get subType(): PDFElementSubType | null {
        return this.fields.sub_type;
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.template) {
                this.template = (
                    props.template instanceof PDFTemplate ? props.template : new PDFTemplate(props.template)
                ).addElement(this);
            }
        }
    };
}
